import { skipToken, useQuery } from '@tanstack/react-query';
import type { components } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

export type LiveNotificationType =
  components['schemas']['services_ui_api_live_notification_types_liveNotificationType'];

export const liveNotificationTypeKeys = {
  list: (workspaceId: number) => ['workspace', workspaceId, 'live-notification-type', 'list'] as const,
  inactive: () => ['live-notification-type', 'inactive'] as const,
};

/** @public */
export async function getLiveNotificationTypes(workspaceId: number): Promise<LiveNotificationType[]> {
  const raw = await hydraUiApi.liveNotificationTypes.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return raw.live_notification_types;
}

/**
 * The workspace's live-notification type registrations, for labeling the
 * `_cio_pts_*` device-attribute markers. Fetched only when `enabled` (callers
 * gate on a marker actually being present); a failed load yields no labels
 * rather than an error state — the markers fall back to their raw ids.
 *
 * @public
 */
export function useLiveNotificationTypesOptional(workspaceId: number, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: enabled ? liveNotificationTypeKeys.list(workspaceId) : liveNotificationTypeKeys.inactive(),
    queryFn: enabled ? () => getLiveNotificationTypes(workspaceId) : skipToken,
    staleTime: 60_000,
    retry: false,
  });
}
